import { z } from 'zod';
import { IsoDateTime } from './common.js';
import { FileKind } from './files.js';
import { PersonRef } from './people.js';
import { Role } from './roles.js';
import { WorkItemType } from './work-items.js';
/** Search across the workspace (docs/adr/0012-search.md, docs/adr/0036-search-by-meaning.md, docs/adr/0037-search-files.md). */
export const SearchKind = z.enum(['all', 'messages', 'work', 'channels', 'people', 'files']);
export const SearchQuery = z.object({
    q: z.string().trim().min(2).max(200),
    kind: SearchKind.default('all'),
    limit: z.coerce.number().int().min(1).max(50).default(8),
    /** Only messages written by this person (tasks and tickets: reported by them; files: uploaded by them). */
    from: z.uuid().optional(),
    /** Only this channel (messages, and files posted in it) or tasks and tickets linked to it (and their files). */
    in: z.uuid().optional(),
    /** Only things created at or after this instant. Clients turn "last week" into instants in the person's time zone. */
    after: IsoDateTime.optional(),
    /** Only things created before this instant. */
    before: IsoDateTime.optional(),
    /**
     * '1' also runs "similar meaning" search (docs/adr/0036). Off by default, so typing in ⌘K stays instant;
     * clients ask for it on the results page and after a pause in the palette.
     */
    meaning: z.enum(['0', '1']).default('0'),
});
/** A piece of text; `match` marks the words that matched the query. Render as text, never as HTML. */
export const Segment = z.object({ text: z.string(), match: z.boolean() });
export const MessageHit = z.object({
    id: z.uuid(),
    channelId: z.uuid(),
    /** Null for a direct message. */
    channelName: z.string().nullable(),
    dmWith: PersonRef.nullable(),
    author: PersonRef.nullable(),
    snippet: z.array(Segment),
    createdAt: z.string(),
});
export const WorkItemHit = z.object({
    id: z.uuid(),
    key: z.string(),
    /** The query was this item's key or ticket number, so highlight the key. */
    keyMatch: z.boolean(),
    type: WorkItemType,
    title: z.array(Segment),
    /** A matching part of the description, when the title did not match. */
    snippet: z.array(Segment).nullable(),
    statusLabel: z.string(),
    statusColor: z.string(),
    projectName: z.string(),
    assignee: PersonRef.nullable(),
    done: z.boolean(),
});
export const ChannelHit = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: z.enum(['client', 'project', 'team']),
    topic: z.string(),
});
export const PersonHit = PersonRef.extend({ role: Role });
/** Where a file found by search lives (docs/adr/0037-search-files.md). */
export const FileLocation = z.discriminatedUnion('type', [
    /** Posted with a message. `channelName` is null for a direct message, which has `dmWith` instead. */
    z.object({ type: z.literal('message'), messageId: z.uuid(), channelId: z.uuid(), channelName: z.string().nullable(), dmWith: PersonRef.nullable() }),
    /** Attached to a task or ticket, or to a customer email on a ticket. `channelId` is the channel the item is linked to. */
    z.object({ type: z.literal('work_item'), workItemId: z.uuid(), key: z.string(), title: z.string(), channelId: z.uuid().nullable() }),
    /** Your own upload, not attached to anything yet. */
    z.object({ type: z.literal('upload') }),
]);
export const FileHit = z.object({
    id: z.uuid(),
    /** The file name with the matching words marked. */
    name: z.array(Segment),
    kind: FileKind,
    contentType: z.string(),
    sizeBytes: z.number().int(),
    location: FileLocation,
    uploadedBy: PersonRef.nullable(),
    createdAt: z.string(),
    /** Matching text from inside the file, when the words matched there (meaning results: the start of the closest passage). */
    snippet: z.array(Segment).nullable(),
});
/** How close a "similar meaning" result is. Only results above the workspace-wide floor are returned at all. */
export const Closeness = z.enum(['close', 'related']);
export const MeaningMessageHit = MessageHit.extend({ closeness: Closeness });
export const MeaningWorkItemHit = WorkItemHit.extend({ closeness: Closeness });
export const MeaningFileHit = FileHit.extend({ closeness: Closeness });
/**
 * "Similar meaning" results (docs/adr/0036): things that don't contain the words but mean something close.
 * Never repeats an exact result. Status says why the list is empty when it is:
 * - `not_requested`: the request had meaning=0;
 * - `off`: the workspace switched it off (`search.byMeaning`), or this server has no embedding provider;
 * - `unavailable`: the provider failed or timed out; exact results are still complete;
 * - `ok`: searched (the lists may still be empty when nothing was close enough).
 */
export const MeaningResults = z.object({
    status: z.enum(['ok', 'not_requested', 'off', 'unavailable']),
    messages: z.array(MeaningMessageHit),
    workItems: z.array(MeaningWorkItemHit),
    /** Files whose text means something close (docs/adr/0037). Older servers leave it out. */
    files: z.array(MeaningFileHit).default([]),
});
export const SearchResults = z.object({
    messages: z.array(MessageHit),
    workItems: z.array(WorkItemHit),
    channels: z.array(ChannelHit),
    people: z.array(PersonHit),
    /** Files by name or by the text inside them (docs/adr/0037). Older servers leave it out. */
    files: z.array(FileHit).default([]),
    meaning: MeaningResults,
});
/** Letters-and-digits words of a query, at most 8, lower case. Everything else is ignored. */
export function searchTerms(q) {
    return [...new Set((q.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []).filter((w) => w.length >= 1))].slice(0, 8);
}
/** Short or very common words match whole words only; "the" should not find "theme" and "then". */
const WHOLE_WORD_ONLY = new Set(['a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'in', 'is', 'it', 'of', 'on', 'or', 'the', 'to', 'was', 'we', 'with', 'you']);
/** Whether a term matches as a word prefix (true) or only as a whole word (false). */
export function isPrefixTerm(term) {
    return term.length >= 3 && !WHOLE_WORD_ONLY.has(term);
}
/**
 * Marks the words that start with a query term and, for long text, keeps a window around the first match.
 * Matching is by prefix, like the database query, so "invoi" highlights "invoice" and "invoices".
 */
export function highlight(text, terms, maxLength = 160) {
    const parts = text.split(/([\p{L}\p{N}]+)/u);
    const isHit = (w) => {
        const lw = w.toLowerCase();
        return terms.some((t) => (isPrefixTerm(t) ? lw.startsWith(t) : lw === t));
    };
    let start = 0;
    if (text.length > maxLength) {
        let pos = 0;
        let first = -1;
        for (const p of parts) {
            if (first < 0 && p && isHit(p))
                first = pos;
            pos += p.length;
        }
        if (first > 40)
            start = text.lastIndexOf(' ', first - 30) + 1;
    }
    const out = [];
    let pos = 0;
    let used = 0;
    for (const p of parts) {
        const end = pos + p.length;
        if (end <= start || !p) {
            pos = end;
            continue;
        }
        const piece = pos < start ? p.slice(start - pos) : p;
        if (used + piece.length > maxLength) {
            out.push({ text: `${piece.slice(0, Math.max(0, maxLength - used)).trimEnd()}…`, match: false });
            used = maxLength;
            break;
        }
        out.push({ text: piece, match: /[\p{L}\p{N}]/u.test(piece) && isHit(piece) });
        used += piece.length;
        pos = end;
    }
    if (start > 0)
        out.unshift({ text: '…', match: false });
    return out.reduce((acc, seg) => {
        const last = acc[acc.length - 1];
        if (last && last.match === seg.match)
            last.text += seg.text;
        else if (seg.text)
            acc.push({ ...seg });
        return acc;
    }, []);
}
//# sourceMappingURL=search.js.map